/**
 *
 * AccountSettings
 *
 */
import * as React from 'react';
import { Box, Button, Paper } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import clsx from 'clsx';
import { FormBoxContainer } from 'styles/theme/themes';
import { PasswordField } from 'app/components/PasswordField';
import Yup from 'utils/yup';
import { useAccountSettingsSlice } from './slice';
import { selectChangePassword } from './slice/selectors';
import { useSnackbar } from 'notistack';

interface Props {}

export function ChangePassword(props: Props) {
  const dispatch = useDispatch();
  const { actions } = useAccountSettingsSlice();
  const changePassword = useSelector(selectChangePassword);
  const schema = Yup.object().shape({
    old_password: Yup.string().required('Current password required'),
    new_password: Yup.string()
      .required('New password required')
      .checkPassword(),
    password_confirmation: Yup.string()
      .required('Confirm password required')
      .oneOf([Yup.ref('new_password'), null], 'Passwords must match'),
  });

  const resetPassword = values => {
    dispatch(actions.changePasswordRequest(values));
  };
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  React.useEffect(() => {
    if (changePassword.loading) return;
    if (changePassword.data) {
      enqueueSnackbar('Password changed successfully.', {
        variant: 'success',
      });
      dispatch(actions.changePasswordMount());
    } else if (changePassword.error) {
      enqueueSnackbar(changePassword.error.message, {
        variant: 'error',
      });
    } else {
      closeSnackbar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changePassword.loading, enqueueSnackbar]);

  return (
    <Box sx={{ my: 4 }}>
      <Paper sx={{ mt: 2, p: 4 }}>
        <Formik
          initialValues={{
            old_password: '',
            new_password: '',
            password_confirmation: '',
          }}
          validationSchema={schema}
          validateOnChange
          onSubmit={(values, { setSubmitting }) => {
            resetPassword(values);
          }}
        >
          {({ submitForm, isSubmitting, setSubmitting, dirty, resetForm }) => {
            if (isSubmitting !== changePassword.loading) {
              setSubmitting(changePassword.loading);
            }

            if (changePassword.data && dirty) {
              resetForm();
            }

            return (
              <Form>
                <FormBoxContainer className={clsx('has-actions')}>
                  <Box className={'formContent'}>
                    <Field
                      component={PasswordField}
                      label={'Current password'}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      id="old_password"
                      name="old_password"
                      className={'field'}
                    />
                    <Field
                      component={PasswordField}
                      label={'New password'}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      id="new_password"
                      name="new_password"
                      className={'field'}
                    />
                    <Field
                      component={PasswordField}
                      label={'Confirm new password'}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      id="password_confirmation"
                      name="password_confirmation"
                      className={'field'}
                    />
                  </Box>
                  <Box>
                    <Box>
                      <Box>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={isSubmitting}
                          fullWidth
                        >
                          Save new password
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </FormBoxContainer>
              </Form>
            );
          }}
        </Formik>
      </Paper>
    </Box>
  );
}
